import { Logo } from "./Logo";
import { Facebook, Instagram, Twitter, ShieldCheck, Award, Leaf, Star, ThumbsUp, Globe, ChevronDown } from "lucide-react";
import { Link, useLocation as useRouterLocation, useNavigate } from "react-router-dom";
import { // Build check trigger
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { useLocation } from "@/lib/locations";
import { useState, startTransition } from "react";
import { cn } from "@/lib/utils";

const EN_TO_ES: Record<string, string> = {
  "/": "/es/sacramento",
  "/locations/sacramento-area": "/es/sacramento",
  "/locations/roseville": "/es/roseville",
  "/locations/pasadena": "/es/pasadena",
  "/locations/los-angeles": "/es/los-angeles",
  "/locations/san-jose": "/es/san-jose",
  "/locations/san-diego": "/es/san-diego",
  "/locations/san-francisco": "/es/san-francisco",
  "/locations/irvine": "/es/irvine",
  "/locations/oakland": "/es/oakland",
  "/locations/long-beach": "/es/long-beach",
  "/locations/contra-costa": "/es/contra-costa",
  "/locations/solano": "/es/solano",
  "/locations/alameda": "/es/alameda",
  "/locations/fremont": "/es/fremont",
  "/locations/hayward": "/es/hayward",
  "/locations/berkeley": "/es/berkeley",
  "/locations/pleasanton": "/es/pleasanton",
  "/locations/dublin": "/es/dublin",
  "/locations/livermore": "/es/livermore",
  "/locations/san-ramon": "/es/san-ramon",
  "/locations/orange-county": "/es/orange-county",
  "/locations/santa-clara": "/es/santa-clara",
  "/locations/marin": "/es/marin",
  "/pricing": "/es/precios",
  "/faq": "/es/preguntas-frecuentes",
};

const ES_TO_EN: Record<string, string> = Object.fromEntries(
  Object.entries(EN_TO_ES).map(([en, es]) => [es, en])
);

// Multilingual pages grouped by region for the clean footer grid
const MULTILINGUAL_REGIONS = [
  {
    label: "Sacramento",
    cities: [
      { name: "Sacramento", es: "/es/sacramento", ru: "/ru/sacramento", zh: "/zh/sacramento" },
      { name: "Roseville",  es: "/es/roseville",  ru: "/ru/roseville",  zh: "/zh/roseville"  },
      { name: "Folsom",     es: "/es/folsom",     ru: "/ru/folsom",     zh: "/zh/folsom"     },
      { name: "Elk Grove",  es: "/es/elk-grove",  ru: "/ru/elk-grove",  zh: "/zh/elk-grove"  },
    ],
  },
  {
    label: "Los Ángeles",
    cities: [
      { name: "Pasadena",       es: "/es/pasadena",       ru: "/ru/pasadena",       zh: "/zh/pasadena"       },
      { name: "Santa Mónica",   es: "/es/santa-monica",   ru: "/ru/santa-monica",   zh: "/zh/santa-monica"   },
      { name: "Long Beach",     es: "/es/long-beach",     ru: "/ru/long-beach",     zh: "/zh/long-beach"     },
      { name: "Sherman Oaks",   es: "/es/sherman-oaks",   ru: "/ru/sherman-oaks",   zh: "/zh/sherman-oaks"   },
    ],
  },
  {
    label: "Bay Area",
    cities: [
      { name: "San Francisco", es: "/es/san-francisco", ru: "/ru/san-francisco", zh: "/zh/san-francisco" },
      { name: "Oakland",       es: "/es/oakland",       ru: "/ru/oakland",       zh: "/zh/oakland"       },
      { name: "San José",      es: "/es/san-jose",      ru: "/ru/san-jose",      zh: "/zh/san-jose"      },
      { name: "Fremont",       es: "/es/fremont",       ru: "/ru/fremont",       zh: "/zh/fremont"       },
    ],
  },
  {
    label: "So. California",
    cities: [
      { name: "San Diego",     es: "/es/san-diego",     ru: "/ru/san-diego",     zh: "/zh/san-diego"     },
      { name: "Irvine",        es: "/es/irvine",        ru: "/ru/irvine",        zh: "/zh/irvine"        },
      { name: "Anaheim",       es: "/es/anaheim",       ru: "/ru/anaheim",       zh: "/zh/anaheim"       },
      { name: "Huntington Bch",es: "/es/huntington-beach",ru: "/ru/huntington-beach",zh: "/zh/huntington-beach" },
    ],
  },
];

const FLAG_ICONS: Record<string, string> = { es: "🇲🇽", ru: "🇷🇺", zh: "🇨🇳" };
const LANG_LABELS: Record<string, string> = { es: "ES", ru: "RU", zh: "中文" };

function MultilingualGrid() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8 border border-border rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-muted/40 hover:bg-muted/70 transition-colors text-sm font-bold"
      >
        <span className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          Available in — ES · RU · 中文
        </span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", open && "rotate-180")} />
      </button>

      {open && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-border border-t border-border">
          {MULTILINGUAL_REGIONS.map(region => (
            <div key={region.label} className="p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">{region.label}</p>
              <ul className="space-y-2">
                {region.cities.map(city => (
                  <li key={city.name}>
                    <p className="text-xs font-semibold text-foreground mb-1">{city.name}</p>
                    <div className="flex gap-2">
                      {(["es", "ru", "zh"] as const).map(lang => (
                        <Link
                          key={lang}
                          to={city[lang]}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                        >
                          {FLAG_ICONS[lang]} {LANG_LABELS[lang]}
                        </Link>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const Footer = () => {
  const location = useLocation();
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();
  const pathname = routerLocation.pathname;
  const isEs = pathname.startsWith("/es/");
  const isRu = pathname.startsWith("/ru/");
  const isZh = pathname.startsWith("/zh/");
  const isNonEn = isEs || isRu || isZh;

  const t = (en: string, es: string, ru: string, zh: string) =>
    isEs ? es : isRu ? ru : isZh ? zh : en;

  const getLocalizedPath = (path: string) => {
    if (path.startsWith("http") || path.startsWith("mailto:") || path.startsWith("tel:") || path.startsWith("#")) {
      return path;
    }
    const prefix = isEs ? "/es" : isRu ? "/ru" : isZh ? "/zh" : "";
    if (!prefix) return path;
    if (path.startsWith(`${prefix}/`) || path === prefix) return path;
    if (path === "/") return prefix;
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    if (cleanPath.startsWith("es/") || cleanPath.startsWith("ru/") || cleanPath.startsWith("zh/")) {
      return `/${cleanPath}`;
    }
    return `${prefix}/${cleanPath}`;
  };

  const handleLanguageSwitch = (lang: "en" | "es" | "ru" | "zh") => {
    localStorage.setItem("langPref", lang);
    const current = pathname;
    if (lang === "en") {
      const target = ES_TO_EN[current] || "/";
      startTransition(() => { navigate(target); });
    } else if (lang === "es") {
      const target = EN_TO_ES[current] || "/es/sacramento";
      startTransition(() => { navigate(target); });
    } else if (lang === "ru") {
      startTransition(() => { navigate("/ru/sacramento"); });
    } else {
      startTransition(() => { navigate("/zh/sacramento"); });
    }
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetPath: string) => {
    if (pathname === targetPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const TRUST_BADGES = [
    { icon: <ShieldCheck className="h-7 w-7 text-primary" />, label: t("Insured & Bonded","Asegurados y Bonificados","Застрахованы","持牌投保") },
    { icon: <Award className="h-7 w-7 text-primary" />, label: t("Top Professionals","Profesionales de Primera","Лучшие специалисты","顶级专业人员") },
    { icon: <Leaf className="h-7 w-7 text-primary" />, label: t("Eco-Friendly Products","Productos Ecológicos","Экопродукты","环保产品") },
    { icon: <Star className="h-7 w-7 text-primary" />, label: t("Premium Quality","Calidad Premium","Премиум Качество","优质品质") },
    { icon: <ThumbsUp className="h-7 w-7 text-primary" />, label: t("200% Guarantee","Garantía del 200%","Гарантия 200%","200%保证") },
  ];

  return (
    <footer className="bg-muted py-16 pb-32 border-t">
      <div className="container">
        {/* Trust Badges Carousel */}
        <div className="mb-16 border-b border-border/50 pb-12">
          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent>
              {TRUST_BADGES.map((badge, index) => (
                <CarouselItem key={index} className="basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5">
                  <div className="flex flex-col items-center justify-center text-center p-4 h-full">
                    <div className="h-16 w-16 bg-background rounded-full flex items-center justify-center shadow-sm mb-4 border">
                      {badge.icon}
                    </div>
                    <span className="font-semibold text-sm text-foreground">{badge.label}</span>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1 flex flex-col gap-6">
            <Link to={getLocalizedPath("/")} onClick={(e) => handleNavClick(e, getLocalizedPath("/"))}>
              <Logo />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t(
                "Professional home cleaning services for your local area. We clean. You relax.",
                "Servicios de limpieza profesional para su área local. Nosotros limpiamos. Usted descansa.",
                "Профессиональный клининг в вашем районе. Мы убираем — вы отдыхаете.",
                "为您所在地区提供专业家庭清洁服务。我们来清洁，您来享受。"
              )}
            </p>
            <div className="flex gap-4">
              <a href="#" aria-label="Twitter" className="p-2 rounded-full bg-background hover:bg-primary/10 transition-colors"><Twitter className="h-5 w-5 text-primary" aria-hidden="true" /></a>
              <a href="#" aria-label="Facebook" className="p-2 rounded-full bg-background hover:bg-primary/10 transition-colors"><Facebook className="h-5 w-5 text-primary" aria-hidden="true" /></a>
              <a href="#" aria-label="Instagram" className="p-2 rounded-full bg-background hover:bg-primary/10 transition-colors"><Instagram className="h-5 w-5 text-primary" aria-hidden="true" /></a>
            </div>
          </div>

          {/* Services */}
          <div className="col-span-1">
            <h3 className="font-bold mb-4 md:mb-6">{t("Services","Servicios","Услуги","服务")}</h3>
            <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
              <li><Link to={getLocalizedPath("/services")} onClick={(e) => handleNavClick(e, getLocalizedPath("/services"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Home Cleaning","Limpieza del Hogar","Уборка дома","家庭清洁")}</Link></li>
              <li><Link to={getLocalizedPath("/services/deep-clean")} onClick={(e) => handleNavClick(e, getLocalizedPath("/services/deep-clean"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Deep Cleaning","Limpieza Profunda","Генеральная уборка","深度清洁")}</Link></li>
              <li><Link to={getLocalizedPath("/services/move-in-out")} onClick={(e) => handleNavClick(e, getLocalizedPath("/services/move-in-out"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Move-Out Cleaning","Limpieza de Mudanza","Уборка при переезде","搬家清洁")}</Link></li>
              <li><Link to={getLocalizedPath("/services/post-construction-clean")} onClick={(e) => handleNavClick(e, getLocalizedPath("/services/post-construction-clean"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Post-Construction","Post-Construcción","После ремонта","装修后清洁")}</Link></li>
              <li><Link to={isEs ? "/es/precios" : getLocalizedPath("/pricing")} onClick={(e) => handleNavClick(e, isEs ? "/es/precios" : getLocalizedPath("/pricing"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Pricing & Packages","Precios y Paquetes","Цены y пакеты","价格与套餐")}</Link></li>
              <li><Link to={getLocalizedPath("/quiz")} onClick={(e) => handleNavClick(e, getLocalizedPath("/quiz"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Build Your Plan Quiz</Link></li>
              <li><Link to={getLocalizedPath("/custom-checklist")} onClick={(e) => handleNavClick(e, getLocalizedPath("/custom-checklist"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Custom Checklist</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="col-span-1">
            <h3 className="font-bold mb-4 md:mb-6">{t("Company","Empresa","Компания","公司")}</h3>
            <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
              <li><Link to={getLocalizedPath("/")} onClick={(e) => handleNavClick(e, getLocalizedPath("/"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Home","Inicio","Главная","首页")}</Link></li>
              <li><Link to={getLocalizedPath("/why-choose-us")} onClick={(e) => handleNavClick(e, getLocalizedPath("/why-choose-us"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Why Choose Us</Link></li>
              <li><Link to={getLocalizedPath("/uplevel-standard")} onClick={(e) => handleNavClick(e, getLocalizedPath("/uplevel-standard"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">The Uplevel Standard</Link></li>
              <li><Link to={getLocalizedPath("/customer-stories")} onClick={(e) => handleNavClick(e, getLocalizedPath("/customer-stories"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Client Experiences</Link></li>
              <li><Link to={getLocalizedPath("/gallery")} onClick={(e) => handleNavClick(e, getLocalizedPath("/gallery"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Gallery","Galería","Галерея","画廊")}</Link></li>
              <li><Link to={isEs ? "/es/preguntas-frecuentes" : getLocalizedPath(location.slug === 'sacramento-area' ? '/faq' : `/locations/${location.slug}/faq`)} onClick={(e) => handleNavClick(e, isEs ? "/es/preguntas-frecuentes" : getLocalizedPath(location.slug === 'sacramento-area' ? '/faq' : `/locations/${location.slug}/faq`))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("FAQ","Preguntas","Вопросы","常见问题")}</Link></li>
              <li><Link to={getLocalizedPath("/join-team")} onClick={(e) => handleNavClick(e, getLocalizedPath("/join-team"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Become A Cleaner","Únete al Equipo","Стать клинером","加入团队")}</Link></li>
              <li><Link to={getLocalizedPath("/onboarding")} onClick={(e) => handleNavClick(e, getLocalizedPath("/onboarding"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Onboarding Portal","Portal de Incorporación","Портал адаптации","入职门户")}</Link></li>
              <li><button onClick={() => { const d = document.getElementById('guarantee-modal') as HTMLDialogElement; if(d) d.showModal(); }} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200 text-left">{t("200% Guarantee","Garantía del 200%","Гарантия 200%","200%保证")}</button></li>
            </ul>
          </div>

          {/* Resources & Contact */}
          <div className="col-span-2 md:col-span-1 mt-2 md:mt-0">
            <h3 className="font-bold mb-4 md:mb-6">{t("Resources & Contact","Recursos y Contacto","Ресурсы и Контакты","资源与联系")}</h3>
            <ul className="flex flex-col gap-3 text-sm text-muted-foreground">
              <li><Link to={getLocalizedPath("/locations")} onClick={(e) => handleNavClick(e, getLocalizedPath("/locations"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Service Areas","Áreas de Servicio","Районы обслуживания","服务区域")}</Link></li>
              <li><Link to={getLocalizedPath(location.slug === 'sacramento-area' ? '/blog' : `/locations/${location.slug}/blog`)} onClick={(e) => handleNavClick(e, getLocalizedPath(location.slug === 'sacramento-area' ? '/blog' : `/locations/${location.slug}/blog`))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Blog</Link></li>
              <li><Link to={getLocalizedPath("/cleaning-tips")} onClick={(e) => handleNavClick(e, getLocalizedPath("/cleaning-tips"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Tips & Hacks</Link></li>
              <li><Link to={getLocalizedPath("/offers")} onClick={(e) => handleNavClick(e, getLocalizedPath("/offers"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Special Offers</Link></li>
              <li><Link to={getLocalizedPath("/resources")} onClick={(e) => handleNavClick(e, getLocalizedPath("/resources"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Resources & Guides</Link></li>
              <li><Link to={getLocalizedPath("/sitemap-viewer")} onClick={(e) => handleNavClick(e, getLocalizedPath("/sitemap-viewer"))} className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">{t("Sitemap","Mapa del Sitio","Карта сайта","网站地图")}</Link></li>
              <li className="pt-2"><a href="tel:9162995588" className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200 font-medium text-foreground">(916) 299-5588</a></li>
              <li><a href="mailto:Support@uplevelcleaning.com" className="hover:text-primary hover:translate-x-1 inline-block transition-all duration-200">Support@uplevelcleaning.com</a></li>
              <li className="text-xs mt-1">{t("Serving CA & AZ","Sirviendo a CA y AZ","Обслуживаем CA и AZ","服务于加州和亚利桑那州")}</li>
            </ul>
          </div>
        </div>

        {/* Multilingual Accordion — replaces the old giant list */}
        <MultilingualGrid />

        {/* Bottom bar */}
        <div className="pt-8 mt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
          <p>{t("© 2026 Uplevel Cleaning LLC. All rights reserved.","© 2026 Uplevel Cleaning LLC. Todos los derechos reservados.","© 2026 Uplevel Cleaning LLC. Все права защищены.","© 2026 Uplevel Cleaning LLC. 版权所有。")}</p>

          {/* 4-Language Switcher */}
          <div className="flex items-center gap-1.5 bg-background border border-border rounded-full px-3 py-1.5 shadow-sm">
            <Globe className="h-3.5 w-3.5 text-primary shrink-0 mr-0.5" />
            {(["en","es","ru","zh"] as const).map((lang, i) => (
              <div key={lang} className="flex items-center">
                {i > 0 && <span className="text-border mx-0.5">|</span>}
                <button
                  onClick={() => handleLanguageSwitch(lang)}
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-all ${
                    (lang === "en" && !isNonEn) || (lang === "es" && isEs) || (lang === "ru" && isRu) || (lang === "zh" && isZh)
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {lang === "en" ? "🇺🇸 EN" : lang === "es" ? "🇲🇽 ES" : lang === "ru" ? "🇷🇺 RU" : "🇨🇳 中文"}
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2">
            <Link to={getLocalizedPath("/privacy-policy")} onClick={(e) => handleNavClick(e, getLocalizedPath("/privacy-policy"))} className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">{t("Privacy Policy","Política de Privacidad","Политика конфиденциальности","隐私政策")}</Link>
            <Link to={getLocalizedPath("/terms-of-service")} onClick={(e) => handleNavClick(e, getLocalizedPath("/terms-of-service"))} className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">{t("Terms of Service","Términos de Servicio","Условия обслуживания","服务条款")}</Link>
            <Link to={getLocalizedPath("/cleaning-policies")} onClick={(e) => handleNavClick(e, getLocalizedPath("/cleaning-policies"))} className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">{t("Cleaning Policies","Políticas de Limpieza","Политика уборки","清洁政策")}</Link>
            <Link to={getLocalizedPath("/cancellation-policy")} onClick={(e) => handleNavClick(e, getLocalizedPath("/cancellation-policy"))} className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">{t("Cancellation Policy","Política de Cancelación","Политика отмены","取消政策")}</Link>
            <Link to={getLocalizedPath("/accessibility-statement")} onClick={(e) => handleNavClick(e, getLocalizedPath("/accessibility-statement"))} className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">Accessibility Statement</Link>
            <Link to={getLocalizedPath("/business-info")} onClick={(e) => handleNavClick(e, getLocalizedPath("/business-info"))} className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">Business Info</Link>
            <button onClick={() => { const d = document.getElementById('guarantee-modal') as HTMLDialogElement; if(d) d.showModal(); }} className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">{t("Satisfaction Guarantee","Garantía de Satisfacción","Гарантия удовлетворенности","满意保证")}</button>
            <a href="mailto:support@uplevelcleaning.com" className="hover:text-primary hover:-translate-y-0.5 inline-block transition-all duration-200">{t("Contact Us","Contáctenos","Связаться с нами","联系我们")}</a>
          </div>
        </div>
      </div>

      {/* 200% Guarantee Modal */}
      <dialog id="guarantee-modal" className="backdrop:bg-black/60 p-0 rounded-2xl shadow-2xl max-w-2xl w-full mx-auto outline-none">
        <div className="p-6 md:p-8 bg-card text-card-foreground">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-primary">Our 200% Satisfaction Guarantee</h2>
            <button 
              onClick={() => { const d = document.getElementById('guarantee-modal') as HTMLDialogElement; if(d) d.close(); }}
              className="p-2 hover:bg-muted rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          
          <div className="space-y-4 text-muted-foreground max-h-[60vh] overflow-y-auto pr-2">
            <p className="text-foreground font-medium">
              At UpLevel Cleaning, we believe inviting someone into your home should come with complete peace of mind. That's why we proudly stand behind every visit with our 200% Satisfaction Guarantee. We wouldn't offer a guarantee this strong unless we were fully confident in the quality of our team, our systems, and the results we deliver.
            </p>
            <p>
              We're committed to providing a cleaning experience that feels easy, dependable, and worth recommending to friends and family.
            </p>
            
            <h3 className="text-lg font-bold text-foreground mt-6 mb-2">How the Guarantee Works</h3>
            <p>If anything about your cleaning falls short of expectations, simply let us know within 48 hours of your service.</p>
            <p>Here's what we'll do:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-foreground">Step 1: We Make It Right.</strong> We'll return promptly to address any missed areas or concerns at no additional cost.</li>
              <li><strong className="text-foreground">Step 2: Still Not Satisfied?</strong> If we're unable to resolve the issue to your satisfaction, we'll provide a full refund.</li>
            </ul>
            <p>That's how strongly we stand behind our work.</p>

            <h3 className="text-lg font-bold text-foreground mt-6 mb-2">Is There a Catch?</h3>
            <p>No catch. Our goal is simple: happy clients and spotless homes.</p>
            <p>The only thing we ask is that you notify us as soon as possible if something was missed so we have the opportunity to correct it quickly. We genuinely appreciate the chance to make things right.</p>

            <h3 className="text-lg font-bold text-foreground mt-6 mb-2">A Few Common-Sense Exceptions</h3>
            <p>To keep things fair for everyone, this guarantee does not apply in situations such as:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Concerns reported more than 48 hours after the original appointment</li>
              <li>Attempts to hire our cleaning professionals directly outside of UpLevel Cleaning</li>
              <li>Offensive, threatening, or abusive behavior toward our staff</li>
              <li>Lack of running water or electricity during the scheduled service, preventing us from completing the job properly</li>
            </ul>

            <h3 className="text-lg font-bold text-foreground mt-6 mb-2">Why We Offer This</h3>
            <p>We're not trying to be just another cleaning company—we're working to become the most trusted cleaning service in the area. That only happens when we consistently deliver great results and stand behind our promises.</p>
            <p className="text-foreground font-medium mt-4">If something isn't right, tell us. We'll make it right. That's the UpLevel standard.</p>
          </div>
        </div>
      </dialog>
    </footer>
  );
};
